import React, { useState, useEffect } from 'react';

interface SentimentJourneyData {
  transcript_id: string;
  transcript_title: string;
  created_at: string;
  overall_sentiment: 'positive' | 'negative' | 'neutral';
  confidence_score: number;
  is_current?: boolean;
}

interface SentimentJourneyProps {
  transcriptId: string;
}

const SentimentJourney: React.FC<SentimentJourneyProps> = ({ transcriptId }) => {
  const [journeyData, setJourneyData] = useState<{
    previous: SentimentJourneyData[];
    next: SentimentJourneyData[];
  }>({ previous: [], next: [] });
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState<string>('');

  useEffect(() => {
    if (transcriptId) {
      // Add a small delay to ensure sentiment data is loaded first
      const timer = setTimeout(() => {
        fetchSentimentJourney();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [transcriptId]);

  const fetchSentimentJourney = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/sentiment/journey?transcriptId=${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          data?: {
            previous: SentimentJourneyData[];
            next: SentimentJourneyData[];
            clientName: string;
          };
          error?: string;
        };
        
        if (data.success && data.data) {
          setJourneyData({
            previous: data.data.previous,
            next: data.data.next
          });
          setClientName(data.data.clientName);
        }
      } else if (response.status === 429 && retryCount < 2) {
        // Retry on rate limit with exponential backoff
        setTimeout(() => {
          fetchSentimentJourney(retryCount + 1);
        }, Math.pow(2, retryCount) * 1000);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch sentiment journey:', error);
      
      // Retry on network error
      if (retryCount < 2) {
        setTimeout(() => {
          fetchSentimentJourney(retryCount + 1);
        }, Math.pow(2, retryCount) * 1000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment: string, confidence: number) => {
    const opacity = Math.max(0.4, confidence); // Minimum 40% opacity
    switch (sentiment) {
      case 'positive':
        return `rgba(34, 197, 94, ${opacity})`; // green
      case 'negative':
        return `rgba(239, 68, 68, ${opacity})`; // red
      case 'neutral':
        return `rgba(107, 114, 128, ${opacity})`; // gray
      default:
        return `rgba(107, 114, 128, 0.4)`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderSentimentBlock = (item: SentimentJourneyData, index: number) => (
    <div
      key={item.transcript_id}
      className={`h-10 rounded-sm flex items-center justify-center text-xs font-medium text-white transition-all duration-200 hover:scale-105 ${
        item.is_current ? 'ring-2 ring-blue-400 ring-offset-1' : ''
      }`}
      style={{
        backgroundColor: getSentimentColor(item.overall_sentiment, item.confidence_score),
        minWidth: '40px',
        flex: '1'
      }}
      title={`${item.transcript_title}\n${formatDate(item.created_at)}\n${item.overall_sentiment} (${Math.round(item.confidence_score * 100)}%)`}
    >
      <span className="text-[10px]">
        {Math.round(item.confidence_score * 100)}%
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="mt-3">
        <div className="text-xs text-gray-600 mb-2">Sentiment Journey:</div>
        <div className="flex items-center gap-1">
          <div className="h-10 w-10 bg-gray-200 rounded-sm animate-pulse"></div>
          <div className="text-xs text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  // Don't show anything if there's only one transcript total or no client
  const totalTranscripts = journeyData.previous.length + journeyData.next.length;
  const uniqueTranscripts = new Set([
    ...journeyData.previous.map(t => t.transcript_id),
    ...journeyData.next.map(t => t.transcript_id)
  ]).size;
  
  if (uniqueTranscripts <= 1 || !clientName) {
    return null;
  }

  // Only show Past row if there are multiple transcripts in the past (more than just current)
  const shouldShowPast = journeyData.previous.length > 1;
  
  // Only show Next row if there are transcripts after current
  const shouldShowNext = journeyData.next.length > 1;

  return (
    <div className="mt-3">
      <div className="text-xs text-gray-600 mb-2">
        Sentiment Journey{clientName ? ` - ${clientName}` : ''}:
      </div>
      
      <div className="space-y-1.5">
        {/* Previous + Current row */}
        {shouldShowPast && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500 mr-1">Past</span>
            <div className="flex gap-0.5 flex-1">
              {journeyData.previous.map((item, index) => renderSentimentBlock(item, index))}
            </div>
          </div>
        )}
        
        {/* Next + Current row */}
        {shouldShowNext && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500 mr-1">Next</span>
            <div className="flex gap-0.5 flex-1">
              {journeyData.next.map((item, index) => renderSentimentBlock(item, index))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SentimentJourney; 